<template>
  <div class="register-wrapper">
    <div class="register-container">
      <h2>Register for Mudra</h2>

      <form @submit.prevent="handleRegister" class="register-form">

        <!-- NAME -->
        <input type="text" v-model.trim="name" placeholder="Full Name" required />

        <!-- USERNAME -->
        <div class="username-row">
          <input 
            type="text"
            v-model.trim="username"
            @blur="checkUsername"
            placeholder="Username"
            required
          />
          <span v-if="username && usernameAvailable === true" class="success-msg">✔</span>
          <span v-if="username && usernameAvailable === false" class="error-msg">✖</span>
        </div>

        <!-- REFERRAL (FINAL CLEAN) -->
        <input 
          type="text"
          v-model="referralInput"
          placeholder="Enter Referral Code (optional)"
        />

        <!-- EMAIL -->
        <input type="email" v-model.trim="email" placeholder="Email Address" required />

        <!-- GENDER -->
        <select v-model="gender" required>
          <option value="" disabled>Select Gender</option>
          <option>Male</option>
          <option>Female</option>
          <option>Other</option>
        </select>

        <!-- COUNTRY -->
        <div class="country-section">
          <select v-model="selectedCountry" @change="loadRegions" required>
            <option value="" disabled>Select Country</option>
            <option v-for="(code, name) in countryCodes" :key="name" :value="name">{{ name }}</option>
            <option value="Other">Other</option>
          </select>

          <input
            v-if="selectedCountry === 'Other'"
            type="text"
            v-model="manualCountry"
            placeholder="Enter your Country"
            required
          />
        </div>

        <!-- REGION -->
        <div v-if="selectedCountry" class="region-section">
          <select v-model="selectedRegion" required>
            <option value="" disabled>Select {{ regionLabel }}</option>
            <option v-for="r in regions" :key="r" :value="r">{{ r }}</option>
            <option value="Other">Other</option>
          </select>

          <input
            v-if="selectedRegion === 'Other'"
            type="text"
            v-model="manualRegion"
            placeholder="Enter your Region/State"
            required
          />
        </div>

        <!-- MOBILE -->
        <div class="phone-input">
          <select v-model="countryCode" required>
            <option v-for="(code, name) in countryCodes" :key="name" :value="code">
              {{ code }} - {{ name }}
            </option>
            <option value="Other">Other</option>
          </select>

          <input
            v-if="countryCode === 'Other'"
            type="text"
            v-model="manualCode"
            placeholder="Enter your country code"
            required
          />

          <input
            type="tel"
            v-model.trim="mobile"
            :placeholder="'Mobile Number (' + getHintForCode(countryCode) + ' digits)'"
            :maxlength="getHintForCode(countryCode)"
            required
          />
        </div>

        <!-- PASSWORD -->
        <div class="password-field">
          <input
            :type="showPassword ? 'text' : 'password'"
            v-model="password"
            placeholder="Password"
            minlength="8"
            required
          />
          <span class="eye-icon" @click="togglePassword">
            {{ showPassword ? '🔓' : '🔒' }}
          </span>
        </div>

        <!-- PASSWORD STRENGTH -->
        <p class="strength" :class="passwordStrengthClass">
          {{ passwordStrengthMessage }}
        </p>

        <!-- CONFIRM PASSWORD -->
        <div class="password-field">
          <input
            :type="showConfirmPassword ? 'text' : 'password'"
            v-model="confirmPassword"
            placeholder="Confirm Password"
            required
          />
          <span class="eye-icon" @click="toggleConfirmPassword">
            {{ showConfirmPassword ? '🔓' : '🔒' }}
          </span>
        </div>

        <!-- TERMS -->
        <div class="tnc-box">
          <input type="checkbox" v-model="acceptedTerms" required />
          <label>
            I agree to 
            <span @click="showTerms = true" style="color:#00c8ff;cursor:pointer;">
              Terms & Conditions
            </span>
          </label>
        </div>

        <!-- TERMS MODAL -->
        <div v-if="showTerms" class="modal-overlay">
          <div class="modal-box">
            <h3>Terms & Conditions</h3>
            <div class="modal-content">
              <pre>{{ termsText }}</pre>
            </div>
            <button @click="showTerms = false">Close</button>
          </div>
        </div>

        <!-- REGISTER -->
        <button type="submit" :disabled="loading || !formIsValid || !acceptedTerms">
          {{ loading ? 'Registering...' : 'Register' }}
        </button>

      </form>
    </div>
  </div>
</template>

<script setup>
import { useRoute, useRouter } from "vue-router";
import { ref, computed } from "vue";
import { supabase } from "@/supabase";

const route = useRoute()
const router = useRouter()

const referralCode = route.query.ref || ""
const referralInput = ref(referralCode) // ✅ AUTO + MANUAL

const name = ref("");
const username = ref("");
const usernameAvailable = ref(null);

const email = ref("");
const gender = ref("");
const selectedCountry = ref("");
const manualCountry = ref("");
const selectedRegion = ref("");
const manualRegion = ref("");
const countryCode = ref("+91");
const manualCode = ref("");
const mobile = ref("");
const password = ref("");
const confirmPassword = ref("");
const acceptedTerms = ref(false);
const showTerms = ref(false);
const loading = ref(false);

const showPassword = ref(false);
const showConfirmPassword = ref(false);

const togglePassword = () => (showPassword.value = !showPassword.value);
const toggleConfirmPassword = () => (showConfirmPassword.value = !showConfirmPassword.value);

// ✅ Username check (UPDATED - lowercase + trim)
const checkUsername = async () => {
  if (!username.value) {
    usernameAvailable.value = null;
    return;
  }

  const usernameLower = username.value.trim().toLowerCase();

  const { data } = await supabase
    .from("user_profiles")
    .select("username")
    .eq("username", usernameLower)   // ← lowercase match
    .maybeSingle();

  usernameAvailable.value = !data;
};


// ✅ Trusted Email Domains
const allowedDomains = [
  "gmail.com",
  "yahoo.com",
  "outlook.com",
  "hotmail.com",
  "icloud.com",
  "aol.com",
  "protonmail.com",
  "zoho.com",
  "yandex.com",
  "gmx.com",
  "mail.com",
  "live.com",
  "msn.com",
  "me.com",
  "fastmail.com",
  "tutanota.com",
  "pm.me",
  "rediffmail.com",
  "inbox.com"
];

// eslint-disable-next-line no-unused-vars
function isAllowedEmail(email) {
  const domain = email.split("@")[1]?.toLowerCase();
  return allowedDomains.includes(domain);
}

const regions = ref([]);
function loadRegions() {
  const map = {
    India: ["Gujarat", "Maharashtra", "Delhi", "Tamil Nadu", "Kerala", "Rajasthan"],
    "United States": ["California", "New York", "Texas", "Florida", "Illinois"],
    "United Kingdom": ["London", "Manchester", "Birmingham", "Liverpool"],
    Japan: ["Tokyo", "Osaka", "Kyoto", "Hokkaido"],
    Australia: ["New South Wales", "Victoria", "Queensland", "Tasmania"],
  };
  regions.value = map[selectedCountry.value] || [];
}

const regionLabel = computed(() => {
  if (selectedCountry.value === "India" || selectedCountry.value === "United States") return "State";
  if (selectedCountry.value === "United Kingdom") return "County";
  if (selectedCountry.value === "Australia") return "Territory";
  return "Region";
});

const countryCodes = {
  India: "+91",
  "United States": "+1",
  "United Kingdom": "+44",
  Japan: "+81",
  Chile: "+56",
  Argentina: "+54",
  Russia: "+7",
  Australia: "+61",
  Brazil: "+55",
  France: "+33",
  Spain: "+34",
  Germany: "+49",
  China: "+86",
  "South Korea": "+82",
  Pakistan: "+92",
  "Hong Kong": "+852",
  Romania: "+40",
  Peru: "+51",
  "Czech Republic": "+420",
  Belgium: "+32",
  "South Africa": "+27",
  "New Zealand": "+64",
  Poland: "+48",
  Bulgaria: "+359",
  Sweden: "+46",
  Norway: "+47",
  Ukraine: "+380",
};

const mobileLengthHints = {
  "+91": 10, "+1": 10, "+44": 10, "+81": 10, "+56": 9, "+61": 9,
  "+55": 11, "+33": 9, "+34": 9, "+49": 10, "+86": 11, "+82": 10,
  "+92": 10, "+852": 8, "+40": 10, "+51": 9, "+420": 9, "+32": 9,
  "+27": 9, "+64": 9, "+48": 9, "+359": 9, "+46": 9, "+47": 8,
  "+380": 9, "+7": 10, "+54": 10,
};
const getHintForCode = (code) => mobileLengthHints[code] || 10;

const passwordStrengthMessage = computed(() => {
  if (!password.value) return "";
  const p = password.value;
  if (p.length < 8) return "Weak";
  if (/[A-Za-z]/.test(p) && /\d/.test(p) && /\W/.test(p)) return "Strong";
  if (/[A-Za-z]/.test(p) && /\d/.test(p)) return "Medium";
  return "Weak";
});
const passwordStrengthClass = computed(() => passwordStrengthMessage.value.toLowerCase());

const isMobileValid = computed(() => {
  const requiredLength = getHintForCode(countryCode.value);
  const regex = new RegExp(`^\\d{${requiredLength}}$`);
  return regex.test(mobile.value.trim());
});

const formIsValid = computed(() =>
  name.value.trim() &&
  email.value.trim() &&
  gender.value &&
  (selectedCountry.value !== "Other" || manualCountry.value.trim()) &&
  (selectedRegion.value !== "Other" || manualRegion.value.trim()) &&
  isMobileValid.value &&
  password.value.length >= 8 &&
  password.value === confirmPassword.value &&
  acceptedTerms.value
);

const termsText = ref(`
MUDRA – TERMS & CONDITIONS & PRIVACY POLICY

🔹 1. Introduction

1.1 Welcome to the Mudra platform (“Mudra”, “we”, “our”, “service”).
1.2 By accessing or using the platform, the user (“you”) agrees to all rules below.
1.3 If you disagree with any point, you must stop using the platform immediately.

🔹 2. Eligibility

2.1 User must be at least 13 years old.
2.2 User must provide correct information.
2.3 Mudra reserves the right to deny service to anyone for any reason.

🔹 3. Nature of Mudra Token

3.1 Mudra Token (MUDRA) is a reward-only digital utility token.
3.2 Mudra Token is not a financial product.
3.3 Mudra Token is not an investment.
3.4 No profit, no return, no guaranteed value.
3.5 Any trading done by users outside the platform is not under Mudra’s control.
3.6 Mudra does not promote or encourage token trading.

🔹 4. Strict Prohibited Activities

User must NOT:

4.1 Upload copyrighted or stolen content.
4.2 Publish abusive, illegal, hateful, or harmful content.
4.3 Attempt to hack, bypass, or manipulate the system.
4.4 Create multiple accounts.
4.5 Manipulate or exploit token rewards.
4.6 Misuse platform for spam or fraud.
4.7 Try to reverse-engineer Mudra’s system.

Violation = instant permanent ban.

🔹 5. Content Responsibility

5.1 All uploaded content is the sole responsibility of the user.
5.2 User confirms they own the rights to their content.
5.3 Mudra is not responsible for any copyright violation done by users.
5.4 Mudra may delete or hide content without notice.
5.5 Mudra may permanently store content for security or legal reasons.

🔹 6. Token Reward System

6.1 Tokens are given as rewards for creative activity.
6.2 Reward rules can change anytime without notice.
6.3 Tokens have no monetary guarantee.
6.4 Tokens do not represent value or ownership.
6.5 Mudra may freeze, adjust, or revoke tokens in case of misuse.

🔹 7. Wallet & Token Transfer

7.1 Users may transfer tokens to their own wallets.
7.2 Withdrawal may include a technical processing fee.
7.3 Mudra is not responsible for incorrect wallet addresses.
7.4 Mudra is not liable for any wallet losses or technical issues.

🔹 8. No Financial Advice

8.1 Mudra gives zero investment advice.
8.2 User must not treat Mudra Token as a financial product.
8.3 User trading decisions are completely their own responsibility.

🔹 9. Limitation of Liability

Mudra is NOT liable for:

9.1 Token losses.
9.2 Trading losses.
9.3 Wallet errors.
9.4 Copyright infringement by users.
9.5 Technical downtime or service issues.
9.6 Data loss.
9.7 User misuse of the platform.

User agrees to use Mudra at their own risk.

🔹 10. Data We Collect

Mudra collects:

10.1 Name
10.2 Email
10.3 Mobile number
10.4 Username
10.5 Uploaded content
10.6 IP address
10.7 Device & usage information

We DO NOT collect:
– Aadhaar
– PAN
– Bank information
– UPI
– Financial KYC

🔹 11. How Data Is Used

11.1 To create and maintain accounts.
11.2 To secure the platform.
11.3 To calculate token rewards.
11.4 To prevent abuse.
11.5 To improve service performance.
11.6 Mudra does NOT sell user data to advertisers.

🔹 12. Cookies

12.1 Mudra uses essential cookies for login and security.
12.2 Cookies cannot be disabled.

🔹 13. User Rights (Strict)

13.1 Users do NOT have the right to delete their stored data.
13.2 Users do NOT have the right to request data export.
13.3 Users do NOT have the right to remove uploaded content.
13.4 Users do NOT have the right to demand account erasure.
13.5 Mudra retains full authority over stored data.

User may request account deactivation, but data may remain stored.

🔹 14. Platform Authority

14.1 Mudra may change, limit, or remove any feature anytime.
14.2 Mudra may ban or suspend any user without explanation.
14.3 Mudra may modify token distribution at any time.
14.4 Mudra has complete control over platform rules.

🔹 15. Policy Updates

15.1 Mudra may update this document without prior notice.
15.2 Continued use of the platform means you accept new terms.

🔹 16. Governing Law

16.1 All rules follow Indian law.

🔹 17. Contact

mudratoken25@gmail.com

mudra-token.web.app
`);

const handleRegister = async () => {
  loading.value = true;

  try {
    // ✅ Email clean
    const emailTrim = email.value.trim().toLowerCase();
    const domain = emailTrim.split("@")[1];

    const blockedDomains = [
      "tempmail.com","10minutemail.com","guerrillamail.com","yopmail.com",
      "mailinator.com","getnada.com","fakeinbox.com","trashmail.com"
    ];

    if (blockedDomains.includes(domain)) {
      alert("❌ Temporary email not allowed");
      return;
    }

    if (!isAllowedEmail(emailTrim)) {
      alert("❌ Only trusted email providers allowed");
      return;
    }

    // ✅ Username check
    if (!username.value || usernameAvailable.value === false) {
      alert("❌ Username not available");
      return;
    }

    const finalUsername = username.value.trim().toLowerCase();

    // ✅ Country / Region / Mobile
    const finalCountry =
      selectedCountry.value === "Other"
        ? manualCountry.value.trim()
        : selectedCountry.value;

    const finalRegion =
      selectedRegion.value === "Other"
        ? manualRegion.value.trim()
        : selectedRegion.value;

    const finalCode =
      countryCode.value === "Other"
        ? manualCode.value.trim()
        : countryCode.value;

    // ✅ Referral (FINAL FIX)
    const finalReferral =
      referralInput.value ? referralInput.value.trim().toLowerCase() : null;

    console.log("👉 FINAL REFERRAL:", finalReferral);

    // 🔥 STEP 1: SIGN UP
    const { data, error } = await supabase.auth.signUp({
      email: emailTrim,
      password: password.value,
      options: {
        emailRedirectTo: null,
        data: {
          full_name: name.value.trim(),
          gender: gender.value,
          country: finalCountry,
          region: finalRegion,
          mobile_number: finalCode + mobile.value.trim(),
        },
      },
    });

    if (error) throw error;

    const userId = data?.user?.id;

    if (!userId) {
      alert("❌ User ID not received");
      return;
    }

    // 🔥 STEP 2: INSERT PROFILE
    const payload = {
      id: userId,
      full_name: name.value.trim(),
      username: finalUsername,
      email: emailTrim,
      gender: gender.value,
      country: finalCountry,
      region: finalRegion,
      mobile_number: finalCode + mobile.value.trim(),
      mudra_tokens: 10, // 🎁 New user bonus
      referral_code: finalUsername,
      referred_by: finalReferral
    };

    console.log("📦 PAYLOAD:", payload);

    const { error: profileError } = await supabase
      .from("user_profiles")
      .upsert(payload, { onConflict: "id" });

    if (profileError) throw profileError;

    // 🎁 STEP 3: REFERRAL REWARD
if (finalReferral) {

  const cleanReferral = finalReferral.toLowerCase().trim();

  console.log("🔍 Searching referrer:", cleanReferral);

  // ⏳ IMPORTANT delay
  await new Promise(r => setTimeout(r, 1000));

  const { data: refUser, error: refError } = await supabase
    .from("user_profiles")
    .select("id, mudra_tokens")
    .ilike("referral_code", cleanReferral) // ✅ FIX
    .maybeSingle();

  console.log("REF RESULT:", refUser, refError);

  if (refUser) {

    const newTokens = Number(refUser.mudra_tokens || 0) + 2;

    const { error: updateError } = await supabase
      .from("user_profiles")
      .update({ mudra_tokens: newTokens })
      .eq("id", refUser.id);

    if (updateError) {
      console.error("❌ Reward failed:", updateError);
    } else {
      console.log("✅ +2 tokens given");
    }

  } else {
    console.log("❌ Referrer not found");
  }

      alert("✅ Registration successful! 🎉\nYou received 10 Mudra Tokens + referral bonus applied!");

    } else {

      alert("✅ Registration successful! 🎉\nYou received 10 Mudra Tokens!");
    }

    router.push("/");

  } catch (err) {
    console.error("❌ ERROR:", err);
    alert("❌ " + (err.message || "Something went wrong"));
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>

/* ------------------------- */
/* MAIN REGISTER CONTAINER */
/* ------------------------- */
.register-container {
  max-width: 460px;
  margin: 9vh auto;
  padding: 36px 30px;
  background: rgba(13, 27, 42, 0.82);
  border-radius: 18px;
  backdrop-filter: blur(10px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.65);
  border: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
  color: #eaf6ff;
  transition: 0.3s ease;
}

h2 {
  margin-bottom: 24px;
  color: #9cdcff;
  font-size: 1.6rem;
  font-weight: 600;
  letter-spacing: 0.3px;
}

/* ------------------------- */
/* FORM ELEMENTS */
/* ------------------------- */
input,
select {
  width: 100%;
  padding: 12px 14px;
  margin-bottom: 14px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  font-size: 15px;
  box-sizing: border-box;
  transition: 0.25s ease;
}

/* 🔥 SAFE SELECT (NO SVG ERROR) */
select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: pointer;
  padding-right: 30px;

  background-image: linear-gradient(45deg, transparent 50%, white 50%),
                    linear-gradient(135deg, white 50%, transparent 50%);
  background-position: calc(100% - 15px) calc(50% - 3px),
                       calc(100% - 10px) calc(50% - 3px);
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
}

/* Dropdown dark */
select option {
  background: #0d1b2a;
  color: #ffffff;
}

/* Selected highlight */
select:focus option:checked {
  background: #00c8ff;
  color: #000;
}

input::placeholder {
  color: rgba(255, 255, 255, 0.7);
}

input:focus,
select:focus {
  outline: none;
  border-color: #00c8ff;
  box-shadow: 0 0 10px rgba(0, 200, 255, 0.45);
}

/* ------------------------- */
/* USERNAME ROW */
/* ------------------------- */
.username-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.username-row input {
  flex: 1;
}

.success-msg {
  color: #00e6a8;
  font-size: 18px;
}

.error-msg {
  color: #ff6b6b;
  font-size: 18px;
}

/* ------------------------- */
/* PHONE INPUT */
/* ------------------------- */
.phone-input {
  display: flex;
  gap: 10px;
}

.phone-input select {
  flex: 1;
  min-width: 120px;
}

.phone-input input {
  flex: 2;
}

/* ------------------------- */
/* PASSWORD FIELD */
/* ------------------------- */
.password-field {
  position: relative;
}

.password-field input {
  width: 100%;
  padding-right: 42px;
}

.eye-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 18px;
  color: #9cdcff;
  opacity: 0.8;
  transition: 0.25s;
}

.eye-icon:hover {
  color: #00c8ff;
  opacity: 1;
}

/* ------------------------- */
/* PASSWORD STRENGTH */
/* ------------------------- */
.strength {
  text-align: left;
  font-size: 13px;
  margin-top: -8px;
  margin-bottom: 10px;
}

.strength.weak { color: #ff7070; }
.strength.medium { color: #ffd966; }
.strength.strong { color: #00e6a8; }

/* ------------------------- */
/* TERMS */
/* ------------------------- */
.tnc-box {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13.5px;
  color: #e8f8ff;
  margin-top: 10px;
  margin-bottom: 16px;
  text-align: left;
  line-height: 1.4;
}

.tnc-box input[type="checkbox"] {
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: #00c8ff;
}

.tnc-link {
  color: #00c8ff;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 600;
}

.tnc-link:hover {
  color: #66e3ff;
}

/* ------------------------- */
/* BUTTON */
/* ------------------------- */
button {
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(90deg, #0077ff, #00c8ff);
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ------------------------- */
/* MODAL */
/* ------------------------- */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-box {
  width: 90%;
  max-width: 650px;
  background: rgba(13, 27, 42, 0.95);
  padding: 24px;
  border-radius: 16px;
  color: #d5e9ff;
}

/* ------------------------- */
/* RESPONSIVE */
/* ------------------------- */
@media (max-width: 480px) {
  .register-container {
    margin: 60px 16px;
    padding: 28px 20px;
  }
}

</style>